import { AxiosError, AxiosResponse } from "axios";
import React from "react";

interface ApiRequestResult<T> {
  data?: T;
  error?: unknown;
}

/**
 * Runs one Explorer request and tracks what the result panel needs: the in-flight flag and the
 * response-or-error. A rejection is not exceptional here — the Explorer exists to show the API's
 * error — so the error body is captured into state for the Error tab rather than rethrown.
 */
export function useApiRequest<T>() {
  const [isLoading, setIsLoading] = React.useState(false);
  const [response, setResponse] = React.useState<ApiRequestResult<T>>({});

  const send = React.useCallback((request: () => Promise<AxiosResponse<T>>, announceSuccess?: (data: T) => void) => {
    setIsLoading(true);
    // Cleared up front so the panel never shows the previous request's result next to a spinner, and
    // started off a resolved promise so a synchronous throw still lands in catch/finally.
    setResponse({});
    void Promise.resolve()
      .then(request)
      .then((result) => {
        announceSuccess?.(result.data);
        setResponse({ data: result.data });
      })
      .catch((error: AxiosError) => {
        setResponse({ error: error?.response?.data ?? error });
      })
      .finally(() => setIsLoading(false));
  }, []);

  return { isLoading, response, send };
}
